import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, Box, Button, Paper, Typography } from '@mui/material';
import { Timeline } from '@mui/icons-material';
import { getLiveRace } from '../services/api';

const LIVE_RED = '#F9696D';
const short = (n: string) =>
  n
    .replace(/[*#]|\([^)]*\)/g, '')
    .trim()
    .split(/\s+/)
    .filter((w) => !/^(jr|sr|ii|iii|iv)\.?$/i.test(w))
    .slice(-1)[0];

/** The Simulator's only live element: a slim strip while a race is on, opening the Race live page. */
export const LiveStrip: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const live = useQuery({
    queryKey: ['liveRaceStrip'],
    queryFn: getLiveRace,
    // One check when the page opens (no polling): the Race live page polls only while it's open.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const race = live.data;
  if (!race?.live) return null;
  const leader = race.drivers?.find((d) => !d.out);
  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', px: isMobile ? 2 : 4, pt: isMobile ? 1.5 : 2 }}>
      <Paper
        sx={{
          p: 1.25,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          border: 1,
          borderColor: alpha(LIVE_RED, 0.45),
          background: (t) =>
            `linear-gradient(90deg, ${alpha(LIVE_RED, t.palette.mode === 'dark' ? 0.12 : 0.08)}, transparent 60%)`,
        }}
      >
        <Box
          component="span"
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            bgcolor: LIVE_RED,
            boxShadow: `0 0 0 4px ${alpha(LIVE_RED, 0.25)}`,
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {race.site ?? race.track} {race.finished ? 'finished' : 'is live'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {race.finished
            ? leader
              ? `${short(leader.driver)} won`
              : 'Final'
            : `Lap ${race.lap}/${race.laps}${leader ? ` · ${short(leader.driver)} leads` : ''}`}
        </Typography>
        <Button
          component={RouterLink}
          to="/live"
          size="small"
          variant="contained"
          startIcon={<Timeline />}
          sx={{ ml: 'auto' }}
        >
          {race.finished ? 'See the results' : 'Follow the race'}
        </Button>
      </Paper>
    </Box>
  );
};
